import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BaseForm, FormInput } from "components/BaseForm";
import { formatError } from "helpers/formatError";
import { useCustomEmailDomainMutations } from "hooks/useCustomEmailDomainsTable";
import CustomEmailDomainsService from "services/CustomEmailDomains";

const schema = z.object({
  domainName: z.string().min(1, "Domain Name is required"),
});

// Keyed by `String(verificationStatus)`; null means the domain was never checked.
const VERIFY_BUTTON_PROPS = {
  true: {
    variant: "default",
    className:
      "tw:bg-success tw:text-primary-foreground tw:hover:bg-success/80",
  },
  false: { variant: "default" },
  null: { variant: "outline" },
};

const VerifyDomainButton = ({
  domain,
  verificationStatus,
  onVerificationStatusChange,
}) => {
  const domainName = useWatch({ name: "domainName" });
  const [isVerifying, setIsVerifying] = useState(false);

  // A renamed domain is unverified until it is checked again.
  useEffect(() => {
    if (domain && domainName !== domain.name) {
      onVerificationStatusChange(false);
    }
  }, [domain, domainName, onVerificationStatusChange]);

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      const response = await CustomEmailDomainsService.verifyDomain(domainName);
      onVerificationStatusChange(response.verified);
      if (response.error) {
        toast.error(response.error);
      }
    } catch (err) {
      toast.error(formatError(err, "Failed to verify domain"));
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            disabled={!domainName || isVerifying}
            onClick={handleVerify}
            {...VERIFY_BUTTON_PROPS[String(verificationStatus)]}
          >
            {verificationStatus ? "Verified" : "Check Verify"}
          </Button>
        }
      />
      <TooltipContent>
        Domain DNS may take 24-48 hours to propagate
      </TooltipContent>
    </Tooltip>
  );
};

VerifyDomainButton.propTypes = {
  domain: PropTypes.shape({ name: PropTypes.string }),
  verificationStatus: PropTypes.bool,
  onVerificationStatusChange: PropTypes.func.isRequired,
};

const DomainForm = ({ domain, onSuccess, onCancel }) => {
  const { create, update } = useCustomEmailDomainMutations();
  const [verificationStatus, setVerificationStatus] = useState(
    domain ? domain.verified : null
  );

  const handleSubmit = async ({ domainName }) => {
    const data = {
      domainName,
      isDefault: domain ? domain.is_default : false,
    };
    if (verificationStatus !== null) {
      data.verificationStatus = verificationStatus;
    }
    if (domain) {
      await update.mutateAsync({ id: domain.id, data });
    } else {
      await create.mutateAsync(data);
    }
    onSuccess();
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={{ domainName: domain ? domain.name : "" }}
      submitButtonText={domain ? "Save" : "Create"}
      submittingButtonText={domain ? "Saving" : "Creating"}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    >
      <div className="tw:flex tw:items-end tw:gap-2">
        <div className="tw:flex-1">
          <FormInput
            name="domainName"
            label="Domain Name"
            placeholder="ex. example.com"
            required
            autoFocus
          />
        </div>
        <VerifyDomainButton
          domain={domain}
          verificationStatus={verificationStatus}
          onVerificationStatusChange={setVerificationStatus}
        />
      </div>
    </BaseForm>
  );
};

DomainForm.propTypes = {
  domain: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    verified: PropTypes.bool,
    is_default: PropTypes.bool,
  }),
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default DomainForm;
